
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { getCurrencySymbol } from '../utils/currencies'
import { BarcodeLabelModal } from '../components/BarcodeLabel'
import { ProductCardModal } from '../components/ProductCard'
import BulkImportProducts from '../components/BulkImportProducts'
import { useTranslation } from '../i18n/LanguageContext'
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'

const ITEMS_PER_PAGE = 10
const CHART_COLORS   = ['#0EA5E9','#2DD4B0','#F59E0B','#EF4444','#7C3AED','#10B981','#F97316','#EC4899']

const STORAGE_KEY = 'pc_inventory_columns_v3'

const ALL_COLUMNS = [
  { id:'sl',        label:'#',           width:'36px',  locked:true },
  { id:'image',     label:'Img',         width:'46px',  locked:true },
  { id:'code',      label:'Code',        width:'105px' },
  { id:'product',   label:'Product',     width:'1.2fr', locked:true },
  { id:'category',  label:'Category',    width:'120px' },
  { id:'stock',     label:'Stock',       width:'110px', locked:true },
  { id:'buy',       label:'Buy Price',   width:'95px'  },
  { id:'sell',      label:'Sell Price',  width:'100px', locked:true },
  { id:'margin',    label:'Margin',      width:'80px'  },
  { id:'vat',       label:'VAT',         width:'65px'  },
  { id:'value',     label:'Stock Value', width:'110px' },
  { id:'added',     label:'Added',       width:'100px' },
  { id:'offer',     label:'Offer',       width:'70px'  },
  { id:'offerpct',  label:'Offer %',     width:'80px'  },
  { id:'offerfrom', label:'Offer From',  width:'100px' },
  { id:'offeruntil',label:'Offer Until', width:'100px' },
  { id:'status',    label:'Status',      width:'100px', locked:true },
  { id:'actions',   label:'Actions',     width:'135px', locked:true },
]
const DEFAULT_VISIBLE = {
  ...Object.fromEntries(ALL_COLUMNS.map(c=>[c.id,true])),
  buy:false, margin:false, vat:false, offerpct:false, offerfrom:false, offeruntil:false,
}

function Icon({ path, size=14, color='var(--text-muted)', sw=1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path}/>
    </svg>
  )
}
function FlatIcon({ path, size=14, color='var(--text-muted)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
      <path fillRule="evenodd" d={path} clipRule="evenodd"/>
    </svg>
  )
}

const IC = {
  eye:      'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z',
  edit:     'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  trash:    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  upload:   'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3',
  plus:     'M12 4v16m8-8H4',
  minus:    'M5 12h14',
  search:   'M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z',
  x:        'M6 18L18 6M6 6l12 12',
  box:      'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  check:    'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  chevL:    'M15 18l-6-6 6-6',
  chevR:    'M9 18l6-6-6-6',
  chevUp:   'M18 15l-6-6-6 6',
  chevDown: 'M6 9l6 6 6-6',
  warning:  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  barcode:  'M4 6h1v12H4V6zm3 0h2v12H7V6zm4 0h1v12h-1V6zm3 0h1v12h-1V6zm3 0h2v12h-2V6zm3 0h1v12h-1V6z',
  image:    'M3 3h18v18H3V3zm2 2v14h14V5H5zm2 5l3 4 2-2 4 5H6l3-4z',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  columns:  'M4 3a1 1 0 00-1 1v16a1 1 0 001 1h3V3H4zm5 0v18h2V3H9zm4 0v18h7a1 1 0 001-1V4a1 1 0 00-1-1h-7z',
  sort:     'M3 7h12M3 12h8M3 17h4M17 4v16m0 0l-4-4m4 4l4-4',
}

const FLAT = {
  box:     'M3 7l7-3.5a1 1 0 01.94 0L18 7l-.94.47L10 4 3.94 7.47 3 7zm0 1.62l7 3.5v6.26l-7-3.5V8.62zm14 0v6.26l-7 3.5v-6.26l7-3.5z',
  check:   'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
  cash:    'M10 18a8 8 0 100-16 8 8 0 000 16zM8.5 7.5a1.5 1.5 0 113 0c0 .56-.31 1.05-.77 1.31l-.73.43V10h-1V9l1.27-.75c.15-.09.23-.25.23-.42a.5.5 0 10-1 0H8.5zm1.5 4a.75.75 0 100 1.5.75.75 0 000-1.5z',
}

function useCountUp(target, duration=600) {
  const [val, setVal] = useState(0)
  const prev = useRef(0)
  useEffect(() => {
    if (target === 0) { setVal(0); return }
    const start = prev.current
    const t0 = performance.now()
    const tick = (now) => {
      const p = Math.min((now-t0)/duration, 1)
      const e = 1-Math.pow(1-p, 3)
      setVal(Math.round(start+(target-start)*e))
      if (p < 1) requestAnimationFrame(tick)
      else prev.current = target
    }
    requestAnimationFrame(tick)
  }, [target, duration])
  return val
}

function StatCard({ label, value, iconPath, accentColor, loading, isMoney, fmt }) {
  const counted = useCountUp(loading?0:(isMoney?0:value))
  return (
    <div className="pc-card pc-card-hover" style={{ padding:'18px 20px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'12px' }}>
        <span style={{ fontSize:'12px', fontWeight:500, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{label}</span>
        <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-md)', background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <FlatIcon path={iconPath} size={15} color={accentColor}/>
        </div>
      </div>
      <div style={{ fontSize:'26px', fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.5px', lineHeight:1, fontFamily:'var(--font-ui)' }}>
        {loading ? <div className="pc-skeleton" style={{ width:'50%', height:'26px', borderRadius:'6px' }}/> : isMoney ? fmt(value) : counted}
      </div>
    </div>
  )
}

function ActionBtn({ onClick, iconPath, title, hoverBg, hoverColor }) {
  const [hov, setHov] = useState(false)
  return (
    <button onClick={onClick} title={title}
      style={{ width:'28px', height:'28px', borderRadius:'var(--radius-sm)', border:'none', background:hov?hoverBg:'transparent', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'var(--transition-fast)', flexShrink:0 }}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}>
      <Icon path={iconPath} color={hov?hoverColor:'var(--text-muted)'} size={13} sw={2}/>
    </button>
  )
}

function FilterableHeader({ label, filterKey, openFilter, setOpenFilter, value, onChange, active }) {
  const isOpen = openFilter===filterKey
  return (
    <div style={{ position:'relative', display:'flex', alignItems:'center', gap:'4px' }}>
      <span style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', fontFamily:'var(--font-ui)' }}>{label}</span>
      <button onClick={(e)=>{ e.stopPropagation(); setOpenFilter(isOpen?null:filterKey) }}
        style={{ width:'14px', height:'14px', display:'flex', alignItems:'center', justifyContent:'center', border:'none', background:'none', cursor:'pointer', padding:0, flexShrink:0 }}>
        <svg width="9" height="9" viewBox="0 0 20 20" fill={active?'var(--primary)':'var(--text-muted)'}>
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.3 5.74V16a1 1 0 01-1.45.89l-2-1A1 1 0 018 15v-3.66L3.2 5.6A1 1 0 013 4z" clipRule="evenodd"/>
        </svg>
      </button>
      {isOpen&&(
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', top:'20px', left:0, zIndex:50, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', padding:'10px', minWidth:'150px' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            <input type="number" value={value.min} onChange={e=>onChange({...value,min:e.target.value})} placeholder="Min"
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}/>
            <input type="number" value={value.max} onChange={e=>onChange({...value,max:e.target.value})} placeholder="Max"
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}/>
          </div>
          {active&&(
            <button onClick={()=>{ onChange({min:'',max:''}); setOpenFilter(null) }}
              style={{ marginTop:'8px', width:'100%', padding:'5px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'11px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ColumnsMenu({ visible, setVisible, open, setOpen }) {
  const hiddenCount = ALL_COLUMNS.filter(c=>!c.locked&&!visible[c.id]).length
  return (
    <div style={{ position:'relative' }}>
      <button onClick={(e)=>{ e.stopPropagation(); setOpen(o=>!o) }}
        style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'var(--radius-md)', border:'1px solid '+(hiddenCount>0?'var(--border-focus)':'var(--border)'), background:hiddenCount>0?'var(--primary-light)':'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:hiddenCount>0?'var(--primary)':'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
        <Icon path={IC.columns} size={13} color={hiddenCount>0?'var(--primary)':'var(--text-primary)'} sw={2}/>
        Columns
        {hiddenCount>0&&<span style={{ fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'var(--radius-full)', background:'var(--primary)', color:'white' }}>{hiddenCount} hidden</span>}
      </button>
      {open&&(
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:60, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', padding:'10px', minWidth:'190px' }}>
          <div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px', fontFamily:'var(--font-ui)' }}>Show columns</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'4px', maxHeight:'280px', overflowY:'auto' }}>
            {ALL_COLUMNS.map(c=>(
              <label key={c.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 6px', borderRadius:'var(--radius-sm)', cursor:c.locked?'default':'pointer', opacity:c.locked?0.5:1 }}>
                <input type="checkbox" checked={!!visible[c.id]} disabled={c.locked}
                  onChange={e=>setVisible(v=>({...v,[c.id]:e.target.checked}))}
                  style={{ accentColor:'var(--primary)', cursor:c.locked?'default':'pointer' }}/>
                <span style={{ fontSize:'12.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{c.label}{c.locked?' (always shown)':''}</span>
              </label>
            ))}
          </div>
          <button onClick={()=>setVisible(DEFAULT_VISIBLE)}
            style={{ marginTop:'8px', width:'100%', padding:'7px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
            Reset to default
          </button>
        </div>
      )}
    </div>
  )
}

function Spinner() {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px' }}>
      <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid var(--primary)', borderTopColor:'transparent', animation:'spin 0.7s linear infinite' }}/>
    </div>
  )
}

function Inventory() {
  const navigate      = useNavigate()
  const { seller }    = useAuth()
  const { showToast } = useToast()
  const { t }         = useTranslation()
  const currency      = seller?.seller_currency || 'BDT'
  const sym           = getCurrencySymbol(currency)

  const [products, setProducts]             = useState([])
  const [loading, setLoading]               = useState(true)
  const [search, setSearch]                 = useState('')
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterStatus, setFilterStatus]     = useState('all')
  const [deleteId, setDeleteId]             = useState(null)
  const [deleting, setDeleting]             = useState(false)
  const [currentPage, setCurrentPage]       = useState(1)
  const [barcodeProduct, setBarcodeProduct] = useState(null)
  const [cardProduct, setCardProduct]       = useState(null)
  const [showBulkImport, setShowBulkImport] = useState(false)
  const [sortBy, setSortBy]                 = useState('newest')
  const [dateFrom, setDateFrom]             = useState('')
  const [dateTo, setDateTo]                 = useState('')
  const [showDateFilter, setShowDateFilter] = useState(false)
  const [openFilter, setOpenFilter]         = useState(null)
  const [priceFilter, setPriceFilter]       = useState({ min:'', max:'' })
  const [columnsOpen, setColumnsOpen]       = useState(false)
  const [visibleCols, setVisibleCols]       = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) return { ...DEFAULT_VISIBLE, ...JSON.parse(saved) }
    } catch {}
    return DEFAULT_VISIBLE
  })
  const fileRef = useRef()
  const [pendingChanges, setPendingChanges] = useState({})
  const [savingChanges, setSavingChanges]   = useState(false)

  useEffect(() => { if (seller?.id) fetchProducts() }, [seller]) // eslint-disable-line
  useEffect(() => { setCurrentPage(1) }, [search, filterCategory, filterStatus, dateFrom, dateTo, priceFilter, sortBy])
  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols)) } catch {} }, [visibleCols])
  useEffect(() => {
    const handler = (e) => { if (Object.keys(pendingChanges).length>0) { e.preventDefault(); e.returnValue='' } }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [pendingChanges])
  useEffect(() => {
    if (!openFilter && !columnsOpen) return
    const handler = () => { setOpenFilter(null); setColumnsOpen(false) }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [openFilter, columnsOpen])

  const fetchProducts = async () => {
    setLoading(true)
    const { data, error } = await supabase.from('products').select('*').eq('seller_id', seller?.id).order('created_at', { ascending:false })
    if (!error) setProducts(data||[])
    setLoading(false)
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    await supabase.from('products').delete().eq('id', deleteId)
    setDeleteId(null); setDeleting(false)
    showToast(t('inventory.toast_deleted'))
    fetchProducts()
  }

  const handleStockAdjust = (product, delta) => {
    const original = pendingChanges[product.id]?.original ?? (product.stock||0)
    const current  = pendingChanges[product.id]?.current  ?? (product.stock||0)
    const newStock = Math.max(0, current+delta)
    setProducts(prev=>prev.map(p=>p.id===product.id?{...p,stock:newStock}:p))
    setPendingChanges(prev=>{
      if (newStock === original) { const { [product.id]:_omit, ...rest } = prev; return rest }
      return { ...prev, [product.id]:{ original, current:newStock, name:product.name } }
    })
  }

  const handleSaveStockChanges = async () => {
    const entries = Object.entries(pendingChanges)
    if (entries.length===0) return
    setSavingChanges(true)
    try {
      await Promise.all(entries.map(([id,{current}])=>supabase.from('products').update({ stock:current }).eq('id',id)))
      await Promise.all(entries.map(([id,{original,current}])=>{
        const delta = current-original
        return supabase.from('stock_log').insert({ product_id:id, action:delta>0?'added':'removed', quantity:Math.abs(delta), note:'Manual stock adjustment via inventory ('+original+' → '+current+')', done_by:'Owner' })
      }))
      showToast(`Stock updated for ${entries.length} product${entries.length!==1?'s':''}`)
      setPendingChanges({})
    } catch(e) { showToast('Error saving stock changes: '+e.message,'error') }
    setSavingChanges(false)
  }

  const handleDiscardStockChanges = () => {
    setProducts(prev=>prev.map(p=>pendingChanges[p.id]?{...p,stock:pendingChanges[p.id].original}:p))
    setPendingChanges({})
  }

  const handleExportCSV = () => {
    const headers = ['SL','Code','Name','Category','Stock','Buy Price','Sell Price','VAT %','Status','Added','Offer','Offer %','Offer From','Offer Until']
    const rows = filtered.map((p,i)=>[
      i+1, p.product_code||'', p.name||'', p.category||'',
      p.stock||0, p.buy_price||0, p.sell_price||0, p.vat_percent||0,
      p.stock===0?t('inventory.status_out'):p.stock<=5?t('inventory.status_low'):t('inventory.status_active'),
      new Date(p.created_at).toLocaleDateString('en-BD'),
      p.offer_active?'Yes':'No',
      p.offer_active&&p.offer_value?(p.offer_type==='percent'?p.offer_value+'%':sym+p.offer_value):'',
      p.offer_valid_from||'', p.offer_valid_until||'',
    ])
    const csv = [headers,...rows].map(r=>r.map(c=>'"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n')
    const blob = new Blob([csv],{type:'text/csv'})
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href=url; a.download='inventory-'+new Date().toISOString().slice(0,10)+'.csv'; a.click()
    URL.revokeObjectURL(url)
    showToast(t('inventory.toast_exported'))
  }

  const handleImportCSV = () => setShowBulkImport(true)

  const fmt = (n) => {
    const abs = Math.abs(Math.round(n||0))
    if (abs>=100000) return sym+Math.round(abs/1000)+'k'
    return sym+abs.toLocaleString()
  }

  const getStatusStyle = (stock) => {
    if (stock===0) return { bg:'var(--error-light)', text:'var(--error)', label:t('inventory.status_out') }
    if (stock<=5)  return { bg:'var(--warning-light)', text:'var(--warning)', label:t('inventory.status_low') }
    return            { bg:'var(--success-light)', text:'var(--success)', label:t('inventory.status_active') }
  }

  const isActiveOffer = (p) => p.offer_active && !(p.offer_valid_until && new Date(p.offer_valid_until) < new Date())

  const categories = ['all', ...new Set(products.map(p=>p.category).filter(Boolean))]
  const resetDateFilter = () => { setDateFrom(''); setDateTo(''); setShowDateFilter(false) }
  const hasDateFilter  = dateFrom||dateTo
  const hasPriceFilter = priceFilter.min||priceFilter.max

  let filtered = products.filter(p => {
    const q = search.toLowerCase()
    const matchSearch = search===''||p.name?.toLowerCase().includes(q)||p.product_code?.toLowerCase().includes(q)||p.category?.toLowerCase().includes(q)
    const matchCat    = filterCategory==='all'||p.category===filterCategory
    const matchStatus = filterStatus==='all'
      ||(filterStatus==='out'    && p.stock===0)
      ||(filterStatus==='low'    && p.stock>0&&p.stock<=5)
      ||(filterStatus==='active' && p.stock>5)
      ||(filterStatus==='offer'  && isActiveOffer(p))
    let matchDate = true
    if (dateFrom) matchDate = matchDate&&new Date(p.created_at)>=new Date(dateFrom)
    if (dateTo) { const to=new Date(dateTo); to.setHours(23,59,59); matchDate=matchDate&&new Date(p.created_at)<=to }
    const price = Number(p.sell_price||0)
    const matchPriceMin = !priceFilter.min||price>=Number(priceFilter.min)
    const matchPriceMax = !priceFilter.max||price<=Number(priceFilter.max)
    return matchSearch&&matchCat&&matchStatus&&matchDate&&matchPriceMin&&matchPriceMax
  })

  filtered = [...filtered].sort((a,b)=>{
    switch(sortBy) {
      case 'stock_asc':  return (a.stock||0)-(b.stock||0)
      case 'stock_desc': return (b.stock||0)-(a.stock||0)
      case 'price_asc':  return (a.sell_price||0)-(b.sell_price||0)
      case 'price_desc': return (b.sell_price||0)-(a.sell_price||0)
      case 'name_asc':   return (a.name||'').localeCompare(b.name||'')
      case 'oldest':     return new Date(a.created_at)-new Date(b.created_at)
      default:           return new Date(b.created_at)-new Date(a.created_at)
    }
  })

  const totalPages = Math.max(1, Math.ceil(filtered.length/ITEMS_PER_PAGE))
  const pageStart  = (currentPage-1)*ITEMS_PER_PAGE
  const pageItems  = filtered.slice(pageStart, pageStart+ITEMS_PER_PAGE)

  const totalValue      = products.reduce((s,p)=>s+Number(p.sell_price||0)*Number(p.stock||0),0)
  const lowStockCount   = products.filter(p=>p.stock>0&&p.stock<=5).length
  const outOfStockCount = products.filter(p=>p.stock===0).length
  const activeCount     = products.filter(p=>p.stock>5).length
  const healthyPct = products.length?(activeCount/products.length*100):0
  const lowPct     = products.length?(lowStockCount/products.length*100):0
  const outPct     = products.length?(outOfStockCount/products.length*100):0

  const catMap = {}
  products.forEach(p=>{ catMap[p.category||'Other']=(catMap[p.category||'Other']||0)+1 })
  const catChartData = Object.entries(catMap).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value).slice(0,6)

  const dayMap = {}
  for (let i=6;i>=0;i--) {
    const d=new Date(); d.setDate(d.getDate()-i)
    const key=d.toLocaleDateString('en-BD',{month:'short',day:'numeric'})
    dayMap[key]=0
  }
  products.forEach(p=>{
    const key=new Date(p.created_at).toLocaleDateString('en-BD',{month:'short',day:'numeric'})
    if(key in dayMap) dayMap[key]++
  })
  const addedChartData = Object.entries(dayMap).map(([day,count])=>({day,count}))

  const visibleColumns  = ALL_COLUMNS.filter(c=>visibleCols[c.id])
  const GRID_TEMPLATE   = visibleColumns.map(c=>c.width).join(' ')
  const TABLE_GAP       = 10
  const FR_MIN_PX       = 220
  const TABLE_MIN_WIDTH = visibleColumns.reduce((sum,c)=>sum+(c.width.endsWith('fr')?FR_MIN_PX:parseInt(c.width)),0)+(visibleColumns.length-1)*TABLE_GAP+32

  return (
    <div className="flex-1 overflow-x-hidden pc-page" style={{ background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
      <Topbar title={t('inventory.title')}/>
      <div style={{ padding:'20px 24px', paddingBottom:'60px' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ fontSize:'20px', fontWeight:800, color:'var(--text-primary)', fontFamily:'var(--font-ui)', letterSpacing:'-0.3px', margin:0 }}>{t('inventory.title')}</h1>
            <p style={{ fontSize:'13px', color:'var(--text-muted)', margin:'3px 0 0', fontFamily:'var(--font-body)' }}>
              {products.length} products · {fmt(totalValue)} total stock value
            </p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>
            <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={handleImportCSV}/>
            <button onClick={handleImportCSV} title="Import CSV"
              style={{ width:'38px', height:'38px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'var(--transition-fast)' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-strong)';e.currentTarget.style.background='var(--bg-muted)'}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-card)'}}>
              <Icon path={IC.upload} size={15} color='var(--text-primary)' sw={2}/>
            </button>
            <button onClick={handleExportCSV} title="Export CSV"
              style={{ width:'38px', height:'38px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'var(--transition-fast)' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-strong)';e.currentTarget.style.background='var(--bg-muted)'}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-card)'}}>
              <Icon path={IC.download} size={15} color='var(--text-primary)' sw={2}/>
            </button>
            <button onClick={()=>navigate('/inventory/add')}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 18px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:'pointer', flexShrink:0, whiteSpace:'nowrap', fontFamily:'var(--font-ui)', boxShadow:'0 2px 8px rgba(14,165,233,0.3)', transition:'var(--transition-base)' }}
              onMouseEnter={e=>{e.currentTarget.style.background='var(--primary-hover)';e.currentTarget.style.boxShadow='0 4px 12px rgba(14,165,233,0.4)'}}
              onMouseLeave={e=>{e.currentTarget.style.background='var(--primary)';e.currentTarget.style.boxShadow='0 2px 8px rgba(14,165,233,0.3)'}}>
              <Icon path={IC.plus} color='white' size={14} sw={2.5}/>{t('inventory.add_product')}
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }} className="inv-stat-grid">
          <StatCard label="Stock Value"                value={totalValue}      iconPath={FLAT.cash}    accentColor='var(--primary)' loading={loading} isMoney fmt={fmt}/>
          <StatCard label={t('inventory.stat_active')} value={activeCount}     iconPath={FLAT.check}   accentColor='var(--success)' loading={loading}/>
          <StatCard label={t('inventory.stat_low')}    value={lowStockCount}   iconPath={FLAT.warning} accentColor='var(--warning)' loading={loading}/>
          <StatCard label={t('inventory.stat_out')}    value={outOfStockCount} iconPath={FLAT.warning} accentColor='var(--error)'   loading={loading}/>
        </div>

        {/* Stock health bar */}
        {products.length>0&&(
          <div className="pc-card" style={{ padding:'14px 18px', marginBottom:'16px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
              <span style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>Stock Health</span>
              <div style={{ display:'flex', gap:'14px', fontSize:'11.5px', fontFamily:'var(--font-body)' }}>
                <span style={{ color:'var(--success)' }}>● Healthy {activeCount}</span>
                <span style={{ color:'var(--warning)' }}>● Low {lowStockCount}</span>
                <span style={{ color:'var(--error)' }}>● Out {outOfStockCount}</span>
              </div>
            </div>
            <div style={{ display:'flex', height:'8px', borderRadius:'var(--radius-full)', overflow:'hidden', background:'var(--bg-muted)' }}>
              {healthyPct>0&&<div style={{ width:healthyPct+'%', background:'var(--success)' }}/>}
              {lowPct>0&&<div style={{ width:lowPct+'%', background:'var(--warning)' }}/>}
              {outPct>0&&<div style={{ width:outPct+'%', background:'var(--error)' }}/>}
            </div>
          </div>
        )}

        {/* Charts */}
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px', marginBottom:'16px' }} className="inv-chart-grid">
          <div className="pc-card" style={{ padding:'16px 20px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-ui)' }}>{t('inventory.chart_added_title')}</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'10px', fontFamily:'var(--font-body)' }}>{t('inventory.chart_added_sub')}</div>
            <ResponsiveContainer width="100%" height={90}>
              <LineChart data={addedChartData} margin={{top:2,right:4,left:-20,bottom:0}}>
                <XAxis dataKey="day" tick={{fontSize:10,fill:'var(--text-muted)',fontFamily:'var(--font-body)'}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:10,fill:'var(--text-muted)',fontFamily:'var(--font-body)'}} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip contentStyle={{ border:'1px solid var(--border)', borderRadius:'8px', fontFamily:'var(--font-body)', fontSize:'11px', background:'var(--bg-card)', color:'var(--text-primary)' }} formatter={v=>[v,t('inventory.chart_added_tooltip')]}/>
                <Line type="monotone" dataKey="count" stroke='var(--primary)' strokeWidth={2.5} dot={{fill:'var(--primary)',r:3,strokeWidth:0}} activeDot={{r:5,strokeWidth:0}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="pc-card" style={{ padding:'16px 20px' }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-ui)' }}>{t('inventory.chart_category_title')}</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'8px', fontFamily:'var(--font-body)' }}>{t('inventory.chart_category_sub')}</div>
            {catChartData.length===0 ? (
              <div style={{ height:'90px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{t('inventory.no_data')}</span>
              </div>
            ) : (
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <ResponsiveContainer width={80} height={80}>
                  <PieChart>
                    <Pie data={catChartData} cx="50%" cy="50%" innerRadius={22} outerRadius={38} paddingAngle={2} dataKey="value" strokeWidth={0}>
                      {catChartData.map((entry,i)=><Cell key={entry.name} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ border:'1px solid var(--border)', borderRadius:'8px', fontFamily:'var(--font-body)', fontSize:'11px', background:'var(--bg-card)', color:'var(--text-primary)' }}/>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'4px' }}>
                  {catChartData.slice(0,4).map((c,i)=>(
                    <div key={c.name} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                        <div style={{ width:'7px', height:'7px', borderRadius:'50%', background:CHART_COLORS[i%CHART_COLORS.length], flexShrink:0 }}/>
                        <span style={{ fontSize:'10.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70px', fontFamily:'var(--font-body)' }}>{c.name}</span>
                      </div>
                      <span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{c.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Category pills */}
        {categories.filter(c=>c!=='all').length>0&&(
          <div style={{ display:'flex', gap:'6px', marginBottom:'12px', overflowX:'auto', paddingBottom:'4px' }}>
            {categories.map(cat=>(
              <button key={cat} onClick={()=>setFilterCategory(cat)}
                style={{ padding:'6px 14px', borderRadius:'var(--radius-full)', border:'1px solid', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap', flexShrink:0, cursor:'pointer', background:filterCategory===cat?'var(--primary-light)':'var(--bg-card)', color:filterCategory===cat?'var(--primary)':'var(--text-muted)', borderColor:filterCategory===cat?'var(--primary)':'var(--border)', transition:'var(--transition-fast)', fontFamily:'var(--font-ui)' }}>
                {cat==='all'?t('inventory.filter_all'):cat}
                <span style={{ fontSize:'10.5px', fontWeight:700, padding:'1px 5px', borderRadius:'var(--radius-full)', marginLeft:'4px', background:filterCategory===cat?'var(--primary)':'var(--bg-muted)', color:filterCategory===cat?'white':'var(--text-muted)' }}>
                  {cat==='all'?products.length:products.filter(p=>p.category===cat).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Search + filters */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:'220px', position:'relative' }}>
            <input type="text" placeholder={t('inventory.search_ph')} value={search} onChange={e=>setSearch(e.target.value)}
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 36px 9px 36px', fontSize:'12.5px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}
              onFocus={e=>{e.target.style.borderColor='var(--border-focus)';e.target.style.background='var(--bg-card)';e.target.style.boxShadow='0 0 0 3px var(--primary-light)'}}
              onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.background='var(--bg-muted)';e.target.style.boxShadow='none'}}/>
            <div style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)' }}>
              <Icon path={IC.search} color='var(--text-muted)' size={14}/>
            </div>
            {search&&<button onClick={()=>setSearch('')} style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px' }}>×</button>}
          </div>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
            style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 12px', fontSize:'12.5px', outline:'none', background:'var(--bg-card)', color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-body)' }}>
            <option value="all">{t('inventory.filter_all_status')}</option>
            <option value="active">{t('inventory.filter_active')}</option>
            <option value="low">{t('inventory.filter_low')}</option>
            <option value="out">{t('inventory.filter_out')}</option>
            <option value="offer">On Offer</option>
          </select>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 12px', fontSize:'12.5px', outline:'none', background:'var(--bg-card)', color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-body)' }}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="stock_asc">Stock: Low to High</option>
            <option value="stock_desc">Stock: High to Low</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name_asc">Name: A-Z</option>
          </select>
          <button onClick={()=>setShowDateFilter(!showDateFilter)}
            style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'var(--radius-md)', border:'1px solid '+(hasDateFilter?'var(--border-focus)':'var(--border)'), background:hasDateFilter?'var(--primary-light)':'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:hasDateFilter?'var(--primary)':'var(--text-primary)', cursor:'pointer', flexShrink:0, fontFamily:'var(--font-ui)' }}>
            <Icon path={IC.calendar} size={13} color={hasDateFilter?'var(--primary)':'var(--text-primary)'} sw={2}/>
            {hasDateFilter?`${dateFrom||'...'} → ${dateTo||'...'}`:'Date added'}
            {hasDateFilter&&<span onClick={e=>{e.stopPropagation();resetDateFilter()}} style={{ marginLeft:'2px', fontSize:'14px', color:'var(--primary)' }}>×</span>}
          </button>
          <ColumnsMenu visible={visibleCols} setVisible={setVisibleCols} open={columnsOpen} setOpen={setColumnsOpen}/>
        </div>

        {/* Date range picker */}
        {showDateFilter&&(
          <div className="pc-card" style={{ padding:'14px 16px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <label style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>From</label>
              <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
                style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12.5px', outline:'none', color:'var(--text-primary)', fontFamily:'var(--font-body)', background:'var(--bg-card)', cursor:'pointer' }}/>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <label style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-ui)' }}>To</label>
              <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
                style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12.5px', outline:'none', color:'var(--text-primary)', fontFamily:'var(--font-body)', background:'var(--bg-card)', cursor:'pointer' }}/>
            </div>
            <button onClick={resetDateFilter}
              style={{ padding:'7px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              Reset
            </button>
            {hasDateFilter&&<div style={{ fontSize:'12px', color:'var(--primary)', fontWeight:600, fontFamily:'var(--font-body)' }}>{filtered.length} product{filtered.length!==1?'s':''} in range</div>}
          </div>
        )}

        {search&&(
          <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'10px', fontFamily:'var(--font-body)' }}>
            {filtered.length} {filtered.length!==1?t('inventory.results'):t('inventory.result')} {t('inventory.results_for')} "{search}"
          </div>
        )}

        {/* Table */}
        {loading ? <Spinner/> : filtered.length===0 ? (
          <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
            <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
              <Icon path={IC.box} color='var(--text-muted)' size={24} sw={1.5}/>
            </div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'6px', fontFamily:'var(--font-ui)' }}>
              {products.length===0?t('inventory.no_products'):t('inventory.no_match')}
            </div>
            {products.length===0&&(
              <button onClick={()=>navigate('/inventory/add')}
                style={{ marginTop:'8px', fontSize:'12.5px', fontWeight:600, padding:'9px 20px', borderRadius:'var(--radius-md)', background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                {t('inventory.add_first')}
              </button>
            )}
          </div>
        ) : (
          <div className="pc-card" style={{ overflow:'hidden' }}>
            <div className="inv-table-scroll" style={{ overflowX:'auto', overflowY:'auto', maxHeight:'640px' }}>
              <div className="inv-table-wrap" style={{ minWidth:TABLE_MIN_WIDTH+'px' }}>

                {/* Header */}
                <div className="inv-th" style={{ display:'grid', gridTemplateColumns:GRID_TEMPLATE, gap:TABLE_GAP+'px', padding:'10px 16px', background:'var(--bg-muted)', borderBottom:'1px solid var(--border)', position:'sticky', top:0, zIndex:10 }}>
                  {visibleColumns.map(col=>{
                    if (col.id==='sell') return <FilterableHeader key={col.id} label={col.label} filterKey="price" openFilter={openFilter} setOpenFilter={setOpenFilter} value={priceFilter} onChange={setPriceFilter} active={!!hasPriceFilter}/>
                    return <span key={col.id} style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', fontFamily:'var(--font-ui)' }}>{col.label}</span>
                  })}
                </div>

                {/* Desktop rows */}
                {pageItems.map((product,idx)=>{
                  const ss         = getStatusStyle(product.stock)
                  const margin     = product.buy_price>0 ? Math.round(((product.sell_price-product.buy_price)/product.sell_price)*100) : null
                  const stockValue = Number(product.sell_price||0)*Number(product.stock||0)
                  const pending    = pendingChanges[product.id]
                  const delta      = pending ? pending.current-pending.original : 0
                  const hasOffer   = isActiveOffer(product)
                  return (
                    <div key={product.id}
                      onClick={()=>navigate('/inventory/'+product.id)}
                      className="inv-desktop"
                      style={{ display:'grid', gridTemplateColumns:GRID_TEMPLATE, gap:TABLE_GAP+'px', padding:'10px 16px', alignItems:'center', cursor:'pointer', transition:'background 0.1s, border-color 0.15s', borderBottom:'1px solid var(--border)', borderLeft:pending?'3px solid var(--warning)':'3px solid transparent', background:pending?'var(--warning-light)':'var(--bg-card)' }}
                      onMouseEnter={e=>{ if(!pending) e.currentTarget.style.background='var(--bg-muted)' }}
                      onMouseLeave={e=>{ if(!pending) e.currentTarget.style.background='var(--bg-card)' }}>

                      {visibleCols.sl&&<div style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', textAlign:'center', fontFamily:'var(--font-body)' }}>{pageStart+idx+1}</div>}

                      {visibleCols.image&&(
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
                          {product.image_url
                            ? <img src={product.image_url+'?t='+(product.updated_at||'')} alt={product.name} style={{ width:'32px', height:'32px', borderRadius:'var(--radius-sm)', objectFit:'cover', border:'1px solid var(--border)' }}/>
                            : <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-sm)', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center' }}><Icon path={IC.box} color='var(--text-muted)' size={14} sw={1.5}/></div>
                          }
                        </div>
                      )}

                      {visibleCols.code&&(
                        <div style={{ fontSize:'11px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {product.product_code||'—'}
                        </div>
                      )}

                      {visibleCols.product&&(
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{product.name}</div>
                          {product.color&&<div style={{ fontSize:'10.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{product.color}</div>}
                        </div>
                      )}

                      {visibleCols.category&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{product.category||'—'}</div>}

                      {visibleCols.stock&&(
                        <div style={{ position:'relative' }} onClick={e=>e.stopPropagation()}>
                          <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                            <button onClick={()=>handleStockAdjust(product,-1)} disabled={savingChanges}
                              style={{ width:'18px', height:'18px', borderRadius:'4px', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:savingChanges?'not-allowed':'pointer', flexShrink:0, opacity:pending?1:0, transition:'opacity 0.12s' }}
                              className="stock-adjust-btn">
                              <Icon path={IC.minus} size={9} color='var(--text-muted)' sw={2.5}/>
                            </button>
                            <div>
                              <div style={{ display:'flex', alignItems:'baseline', gap:'4px' }}>
                                <span style={{ fontSize:'12px', fontWeight:700, color:product.stock===0?'var(--error)':product.stock<=5?'var(--warning)':'var(--success)', fontFamily:'var(--font-body)' }}>{product.stock}</span>
                                {pending&&<span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--warning)', fontFamily:'var(--font-body)' }}>({delta>0?'+':''}{delta})</span>}
                              </div>
                              <div style={{ width:'36px', height:'3px', borderRadius:'2px', background:'var(--bg-muted)', marginTop:'3px' }}>
                                <div style={{ height:'100%', borderRadius:'2px', width:Math.min((product.stock/50)*100,100)+'%', background:product.stock===0?'var(--error)':product.stock<=5?'var(--warning)':'var(--success)' }}/>
                              </div>
                            </div>
                            <button onClick={()=>handleStockAdjust(product,1)} disabled={savingChanges}
                              style={{ width:'18px', height:'18px', borderRadius:'4px', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:savingChanges?'not-allowed':'pointer', flexShrink:0, opacity:pending?1:0, transition:'opacity 0.12s' }}
                              className="stock-adjust-btn">
                              <Icon path={IC.plus} size={9} color='var(--text-muted)' sw={2.5}/>
                            </button>
                          </div>
                        </div>
                      )}

                      {visibleCols.buy&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{product.buy_price?fmt(product.buy_price):'—'}</div>}
                      {visibleCols.sell&&<div style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(product.sell_price||0)}</div>}
                      {visibleCols.margin&&<div style={{ fontSize:'11.5px', fontWeight:600, color:margin===null?'var(--text-muted)':margin<15?'var(--error)':margin<30?'var(--warning)':'var(--success)', fontFamily:'var(--font-body)' }}>{margin===null?'—':margin+'%'}</div>}
                      {visibleCols.vat&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{product.vat_percent?product.vat_percent+'%':'—'}</div>}
                      {visibleCols.value&&<div style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(stockValue)}</div>}
                      {visibleCols.added&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{new Date(product.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'2-digit'})}</div>}

                      {visibleCols.offer&&(
                        <div>
                          {hasOffer
                            ? <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'var(--radius-full)', background:'rgba(45,212,176,0.12)', color:'var(--success)', whiteSpace:'nowrap' }}>Yes</span>
                            : <span style={{ fontSize:'11px', color:'var(--text-muted)' }}>—</span>
                          }
                        </div>
                      )}

                      {visibleCols.offerpct&&(
                        <div style={{ fontSize:'11.5px', fontWeight:600, color:'var(--success)', fontFamily:'var(--font-body)' }}>
                          {hasOffer&&product.offer_value
                            ? product.offer_type==='percent' ? product.offer_value+'%'
                            : product.offer_type==='fixed'   ? sym+product.offer_value
                            : product.offer_label||'—'
                            : '—'}
                        </div>
                      )}

                      {visibleCols.offerfrom&&(
                        <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                          {product.offer_valid_from ? new Date(product.offer_valid_from).toLocaleDateString('en-BD',{day:'numeric',month:'short'}) : '—'}
                        </div>
                      )}

                      {visibleCols.offeruntil&&(
                        <div style={{ fontSize:'11px', color:product.offer_valid_until&&new Date(product.offer_valid_until)<new Date()?'var(--error)':'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                          {product.offer_valid_until ? new Date(product.offer_valid_until).toLocaleDateString('en-BD',{day:'numeric',month:'short'}) : '—'}
                        </div>
                      )}

                      {visibleCols.status&&(
                        <div>
                          <span style={{ fontSize:'10.5px', fontWeight:600, padding:'3px 9px', borderRadius:'var(--radius-full)', background:ss.bg, color:ss.text, whiteSpace:'nowrap', fontFamily:'var(--font-ui)' }}>{ss.label}</span>
                        </div>
                      )}

                      {visibleCols.actions&&(
                        <div style={{ display:'flex', alignItems:'center', gap:'2px' }} onClick={e=>e.stopPropagation()}>
                          <ActionBtn onClick={()=>navigate('/inventory/'+product.id)} iconPath={IC.eye} title={t('inventory.view_btn')} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
                          <ActionBtn onClick={()=>navigate('/inventory/'+product.id+'?edit=1')} iconPath={IC.edit} title={t('inventory.edit_btn')} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
                          <ActionBtn onClick={async()=>{
                            const { data:vars } = await supabase.from('product_variants').select('*').eq('product_id',product.id).order('variant_name')
                            setBarcodeProduct({ product, variants:vars||[] })
                          }} iconPath={IC.barcode} title={t('inventory.title_barcode')} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
                          <ActionBtn onClick={()=>setCardProduct(product)} iconPath={IC.image} title={t('inventory.title_card')} hoverBg='var(--success-light)' hoverColor='var(--success)'/>
                          <ActionBtn onClick={()=>setDeleteId(product.id)} iconPath={IC.trash} title={t('inventory.delete_btn')} hoverBg='var(--error-light)' hoverColor='var(--error)'/>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Mobile cards */}
            {pageItems.map((product,idx)=>{
              const ss         = getStatusStyle(product.stock)
              const margin     = product.buy_price>0 ? Math.round(((product.sell_price-product.buy_price)/product.sell_price)*100) : null
              const stockValue = Number(product.sell_price||0)*Number(product.stock||0)
              const pending    = pendingChanges[product.id]
              const delta      = pending ? pending.current-pending.original : 0
              const hasOffer   = isActiveOffer(product)
              return (
                <div key={'m'+product.id} onClick={()=>navigate('/inventory/'+product.id)}
                  className="inv-mobile" style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer', borderLeft:pending?'3px solid var(--warning)':'3px solid transparent', background:pending?'var(--warning-light)':'var(--bg-card)' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:'10px', marginBottom:'10px' }}>
                    <span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', flexShrink:0, marginTop:'2px' }}>#{pageStart+idx+1}</span>
                    {product.image_url
                      ? <img src={product.image_url+'?t='+(product.updated_at||'')} alt={product.name} style={{ width:'44px', height:'44px', borderRadius:'var(--radius-md)', objectFit:'cover', border:'1px solid var(--border)', flexShrink:0 }}/>
                      : <div style={{ width:'44px', height:'44px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Icon path={IC.box} color='var(--text-muted)' size={18} sw={1.5}/></div>
                    }
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'8px', marginBottom:'2px' }}>
                        <div style={{ minWidth:0, flex:1 }}>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{product.name}</div>
                          {hasOffer&&<span style={{ fontSize:'9.5px', fontWeight:700, padding:'1px 6px', borderRadius:'var(--radius-full)', background:'rgba(45,212,176,0.12)', color:'var(--success)' }}>OFFER</span>}
                        </div>
                        <span style={{ fontSize:'10.5px', fontWeight:600, padding:'2px 8px', borderRadius:'var(--radius-full)', background:ss.bg, color:ss.text, flexShrink:0, fontFamily:'var(--font-ui)', whiteSpace:'nowrap' }}>{ss.label}</span>
                      </div>
                      <div style={{ fontSize:'11px', color:'var(--primary)', fontFamily:'var(--font-mono)', marginBottom:'2px' }}>{product.product_code||'—'}</div>
                      <div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{product.category||'—'}{product.color?' · '+product.color:''}</div>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', paddingTop:'10px', paddingBottom:'10px', borderTop:'1px solid var(--border)' }}>
                    <div onClick={e=>e.stopPropagation()}>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Stock</div>
                      <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                        <button onClick={()=>handleStockAdjust(product,-1)} disabled={savingChanges} style={{ width:'18px', height:'18px', borderRadius:'4px', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:savingChanges?'not-allowed':'pointer', flexShrink:0 }}>
                          <Icon path={IC.minus} size={9} color='var(--text-muted)' sw={2.5}/>
                        </button>
                        <span style={{ fontSize:'12.5px', fontWeight:700, color:product.stock===0?'var(--error)':product.stock<=5?'var(--warning)':'var(--success)', fontFamily:'var(--font-body)' }}>{product.stock}</span>
                        <button onClick={()=>handleStockAdjust(product,1)} disabled={savingChanges} style={{ width:'18px', height:'18px', borderRadius:'4px', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:savingChanges?'not-allowed':'pointer', flexShrink:0 }}>
                          <Icon path={IC.plus} size={9} color='var(--text-muted)' sw={2.5}/>
                        </button>
                        {pending&&<span style={{ fontSize:'10px', fontWeight:700, color:'var(--warning)', fontFamily:'var(--font-body)' }}>({delta>0?'+':''}{delta})</span>}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Buy</div>
                      <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{product.buy_price?fmt(product.buy_price):'—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Sell</div>
                      <div style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(product.sell_price||0)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Margin</div>
                      <div style={{ fontSize:'12.5px', fontWeight:600, color:margin===null?'var(--text-muted)':margin<15?'var(--error)':margin<30?'var(--warning)':'var(--success)', fontFamily:'var(--font-body)' }}>{margin===null?'—':margin+'%'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>VAT</div>
                      <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{product.vat_percent?product.vat_percent+'%':'—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Value</div>
                      <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(stockValue)}</div>
                    </div>
                    {hasOffer&&(
                      <div style={{ gridColumn:'span 3' }}>
                        <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Offer</div>
                        <div style={{ fontSize:'12px', fontWeight:600, color:'var(--success)', fontFamily:'var(--font-body)' }}>
                          {product.offer_value
                            ? product.offer_type==='percent' ? product.offer_value+'% off'
                            : product.offer_type==='fixed'   ? sym+product.offer_value+' off'
                            : product.offer_label||'Active offer'
                            : product.offer_label||'Active offer'}
                          {product.offer_valid_until&&` · until ${new Date(product.offer_valid_until).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}`}
                        </div>
                      </div>
                    )}
                    <div style={{ gridColumn:'span 3' }}>
                      <div style={{ fontSize:'9.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Added</div>
                      <div style={{ fontSize:'12.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{new Date(product.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'numeric'})}</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'6px', paddingTop:'10px', borderTop:'1px solid var(--border)' }} onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>navigate('/inventory/'+product.id)} style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.eye} size={14} color='var(--text-primary)' sw={2}/>
                    </button>
                    <button onClick={()=>navigate('/inventory/'+product.id+'?edit=1')} style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.edit} size={14} color='var(--text-primary)' sw={2}/>
                    </button>
                    <button onClick={async()=>{
                      const { data:vars } = await supabase.from('product_variants').select('*').eq('product_id',product.id).order('variant_name')
                      setBarcodeProduct({ product, variants:vars||[] })
                    }} style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.barcode} size={14} color='var(--text-primary)' sw={2}/>
                    </button>
                    <button onClick={()=>setCardProduct(product)} style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.image} size={14} color='var(--success)' sw={2}/>
                    </button>
                    <button onClick={()=>setDeleteId(product.id)} style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--error-border)', background:'var(--error-light)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <Icon path={IC.trash} size={14} color='var(--error)' sw={2}/>
                    </button>
                  </div>
                </div>
              )
            })}

            {/* Pagination */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', flexWrap:'wrap', gap:'6px' }}>
              <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                {t('inventory.showing')} <strong style={{ color:'var(--text-primary)' }}>{pageStart+1}–{Math.min(pageStart+ITEMS_PER_PAGE,filtered.length)}</strong> {t('inventory.of')} <strong style={{ color:'var(--text-primary)' }}>{filtered.length}</strong> {t('inventory.products_word')}
              </div>
              {totalPages>1&&(
                <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                  <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1}
                    style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:currentPage===1?'not-allowed':'pointer', opacity:currentPage===1?0.4:1 }}>
                    <Icon path={IC.chevL} color='var(--text-primary)' size={13} sw={2}/>
                  </button>
                  {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                    let page
                    if(totalPages<=7) page=i+1
                    else if(currentPage<=4) page=i+1
                    else if(currentPage>=totalPages-3) page=totalPages-6+i
                    else page=currentPage-3+i
                    if(page<1||page>totalPages) return null
                    return (
                      <button key={page} onClick={()=>setCurrentPage(page)}
                        style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid '+(currentPage===page?'var(--primary)':'var(--border)'), background:currentPage===page?'var(--primary)':'var(--bg-card)', color:currentPage===page?'white':'var(--text-primary)', fontSize:'12px', fontWeight:currentPage===page?700:400, cursor:'pointer', fontFamily:'var(--font-body)' }}>
                        {page}
                      </button>
                    )
                  })}
                  <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages}
                    style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:currentPage===totalPages?'not-allowed':'pointer', opacity:currentPage===totalPages?0.4:1 }}>
                    <Icon path={IC.chevR} color='var(--text-primary)' size={13} sw={2}/>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {deleteId&&(
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'16px' }}>
          <div className="animate-scale-in" style={{ background:'var(--bg-card)', borderRadius:'var(--radius-xl)', padding:'24px', maxWidth:'360px', width:'100%', fontFamily:'var(--font-ui)', border:'1px solid var(--border)', boxShadow:'var(--shadow-xl)' }}>
            <div style={{ textAlign:'center', marginBottom:'12px' }}>
              <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'var(--error-light)', border:'1px solid var(--error-border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto' }}>
                <Icon path={IC.trash} color='var(--error)' size={24} sw={1.5}/>
              </div>
            </div>
            <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', textAlign:'center', marginBottom:'6px' }}>{t('inventory.delete_title')}</div>
            <div style={{ fontSize:'12.5px', color:'var(--text-muted)', textAlign:'center', marginBottom:'20px', fontFamily:'var(--font-body)' }}>{t('inventory.delete_desc')}</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>setDeleteId(null)} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                {t('inventory.delete_cancel')}
              </button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', background:'var(--error)', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:'pointer', opacity:deleting?0.6:1, fontFamily:'var(--font-ui)' }}>
                {deleting?t('inventory.deleting'):t('inventory.delete_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {cardProduct&&<ProductCardModal product={cardProduct} shopName={seller?.shop_name||'PageConnect'} onClose={()=>setCardProduct(null)}/>}
      {barcodeProduct&&<BarcodeLabelModal product={barcodeProduct.product} variants={barcodeProduct.variants} shopName={seller?.shop_name||'PageConnect'} onClose={()=>setBarcodeProduct(null)}/>}

      {Object.keys(pendingChanges).length>0&&(
        <div className="animate-scale-in" style={{ position:'fixed', bottom:'24px', left:'50%', transform:'translateX(-50%)', zIndex:200, display:'flex', alignItems:'center', gap:'14px', padding:'12px 16px 12px 20px', borderRadius:'var(--radius-lg)', background:'var(--bg-card)', border:'1px solid var(--warning-border)', boxShadow:'var(--shadow-xl)', maxWidth:'calc(100vw - 32px)' }}>
          <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'var(--warning)', flexShrink:0 }}/>
          <span style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-ui)', whiteSpace:'nowrap' }}>
            {Object.keys(pendingChanges).length} product{Object.keys(pendingChanges).length!==1?'s':''} with unsaved stock changes
          </span>
          <div style={{ display:'flex', gap:'8px', flexShrink:0 }}>
            <button onClick={handleDiscardStockChanges} disabled={savingChanges}
              style={{ padding:'8px 16px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12.5px', fontWeight:600, color:'var(--text-muted)', cursor:savingChanges?'not-allowed':'pointer', fontFamily:'var(--font-ui)' }}>
              Discard
            </button>
            <button onClick={handleSaveStockChanges} disabled={savingChanges}
              style={{ padding:'8px 18px', borderRadius:'var(--radius-md)', border:'none', background:'var(--warning)', fontSize:'12.5px', fontWeight:700, color:'white', cursor:savingChanges?'not-allowed':'pointer', fontFamily:'var(--font-ui)', display:'flex', alignItems:'center', gap:'6px' }}>
              {savingChanges&&<div style={{ width:'12px', height:'12px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'white', animation:'spin 0.6s linear infinite' }}/>}
              {savingChanges?'Saving...':'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {showBulkImport && (
        <BulkImportProducts
          seller={seller}
          onClose={() => setShowBulkImport(false)}
          onSuccess={() => { setShowBulkImport(false); fetchProducts() }}
        />
      )}
      <style>{`
        @keyframes spin { to { transform:rotate(360deg) } }
        .inv-mobile { display: none; }
        .inv-desktop:hover .stock-adjust-btn { opacity: 1 !important; }
        @media(max-width:900px){
          .inv-stat-grid { grid-template-columns: 1fr 1fr !important; }
          .inv-chart-grid { grid-template-columns: 1fr !important; }
        }
        @media(max-width:767px){
          .inv-desktop { display: none !important; }
          .inv-th { display: none !important; }
          .inv-mobile { display: block; }
          .inv-table-wrap { min-width:0!important }
          .inv-table-scroll { overflow-x:visible!important }
        }
      `}</style>
    </div>
  )
}

export default Inventory